import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { WorkspaceCabinet, WorkspacePayload, WorkspaceSection, WorkspaceUpdate } from "@wms/domain";
import { ApiError } from "../api/client";
import { workspaceApi, workspaceKey } from "../api/workspace";
import { useAuthStore } from "../auth/store";
import { usePortalStore } from "../portal/store";

/**
 * ТЗ «Рабочие столы» 31.10.2026: one cache and one save path of the own look for the appearance settings and the
 * desktop. The principal is part of the key (UXW-11, CW-AT-04); the parts not given in a save stay as they are.
 */
export function useWorkspace(cabinet: WorkspaceCabinet, enabled = true) {
  const staffId = useAuthStore((s) => s.me?.user.id);
  const clientId = usePortalStore((s) => s.me?.user.id);
  const principal = cabinet === "portal" ? clientId : staffId;
  const queryClient = useQueryClient();
  const key = workspaceKey(cabinet, principal);
  const query = useQuery({
    queryKey: key,
    queryFn: () => workspaceApi.get(cabinet),
    enabled: enabled && Boolean(principal),
    staleTime: 60_000,
    retry: false,
  });
  const save = useMutation({
    mutationFn: (body: WorkspaceUpdate) => workspaceApi.save(cabinet, body),
    // «Сохранено» only after the server answered (UXW-16): the cache takes the saved section from the answer
    onSuccess: (data) => queryClient.setQueryData<WorkspacePayload>(key, data),
  });
  return { ...query, save, key };
}

/** UXW-17: the section saved from another window, carried by the 409 answer; null for any other error. */
export function conflictOf(error: unknown): WorkspaceSection | null {
  if (!(error instanceof ApiError) || error.problem.status !== 409) return null;
  const current = (error.problem as unknown as { workspace?: WorkspaceSection }).workspace;
  return current ?? null;
}
